/**
 * GoogleDrivePickerWebView — Google Picker UI alternative to GoogleDrivePicker.
 *
 * Loads the Google Picker API directly in the page (no iframe / PHP needed).
 * Uses gdrive_file_refresh_token (drive.file scope, verified) matching the mobile picker.
 * YouTubePicker uses youtube_refresh_token (youtube.readonly) via youtube_auth.php.
 * drive.readonly is NOT used here — it is unverified and shows a scary consent warning.
 */

import React, { useState, useRef, useEffect, useCallback } from "react";
import { API_BASE_URL } from "../../Common/Common";

const API_BASE         = API_BASE_URL;
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS  = 5 * 60 * 1000;
const STORAGE_KEY      = "gdrive_file_refresh_token";
const PICKER_API_KEY   = process.env.REACT_APP_GOOGLE_DRIVE_PICKER_KEY;

function parseDriveTime(str) {
  if (!str) return null;
  const normalized = str.replace(/^(\d{4}):(\d{2}):(\d{2})/, "$1-$2-$3");
  const d = new Date(normalized);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

function mimeTypes(fileType) {
  if (fileType === "video") return "video/mp4,video/quicktime,video/x-msvideo,video/mpeg,video/webm,video/3gpp";
  if (fileType === "audio") return "audio/mpeg,audio/mp4,audio/ogg,audio/wav,audio/x-m4a,audio/aac";
  if (fileType === "pdf")   return "application/pdf";
  return "image/jpeg,image/png,image/gif,image/webp,image/bmp,image/heic,image/heif,image/tiff";
}

function loadGapiScript() {
  return new Promise((resolve, reject) => {
    if (window.gapi) { resolve(); return; }
    const s = document.createElement("script");
    s.src = "https://apis.google.com/js/api.js";
    s.onload = resolve;
    s.onerror = () => reject(new Error("Failed to load Google API script"));
    document.head.appendChild(s);
  });
}

function loadPickerApi() {
  return new Promise((resolve, reject) => {
    window.gapi.load("picker", { callback: resolve, onerror: () => reject(new Error("Failed to load Picker API")) });
  });
}

export default function GoogleDrivePickerWebView({ onSelect, multi = false, fileType = "image" }) {
  const [token,          setToken]          = useState(null);
  const [loading,        setLoading]        = useState(false);
  const [waitingForAuth, setWaitingForAuth] = useState(false);
  const [pickerLoading,  setPickerLoading]  = useState(false);
  const [pickerError,    setPickerError]    = useState(null);

  const pollRef      = useRef(null);
  const pollStartRef = useRef(null);
  const tokenRef     = useRef(null);

  // ── silent token restore ────────────────────────────────────────────────────

  useEffect(() => {
    const refreshToken = localStorage.getItem(STORAGE_KEY);
    if (!refreshToken) return;
    setLoading(true);
    fetch(`${API_BASE}/gdrive_refresh.php`, {
      method:  "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body:    `refresh_token=${encodeURIComponent(refreshToken)}`,
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.access_token) {
          tokenRef.current = data.access_token;
          setToken(data.access_token);
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));

    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // ── auth ────────────────────────────────────────────────────────────────────

  const startAuth = () => {
    const sessionId = crypto.randomUUID();
    window.open(`${API_BASE}/gdrive_auth_picker.php?session=${sessionId}`, "_blank");
    setWaitingForAuth(true);
    pollStartRef.current = Date.now();
    pollRef.current = setInterval(async () => {
      if (Date.now() - pollStartRef.current > POLL_TIMEOUT_MS) {
        clearInterval(pollRef.current);
        setWaitingForAuth(false);
        alert("Sign-in timed out. Please try again.");
        return;
      }
      try {
        const res  = await fetch(`${API_BASE}/gdrive_poll.php?session=${sessionId}`);
        const data = await res.json();
        if (data.access_token) {
          clearInterval(pollRef.current);
          setWaitingForAuth(false);
          tokenRef.current = data.access_token;
          setToken(data.access_token);
          if (data.refresh_token) localStorage.setItem(STORAGE_KEY, data.refresh_token);
        } else if (data.error) {
          clearInterval(pollRef.current);
          setWaitingForAuth(false);
          alert("Sign-in failed: " + data.error);
        }
      } catch { /* keep polling */ }
    }, POLL_INTERVAL_MS);
  };

  const cancelAuth = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setWaitingForAuth(false);
  };

  const signOut = () => {
    localStorage.removeItem(STORAGE_KEY);
    setToken(null);
    tokenRef.current = null;
  };

  // ── open picker ─────────────────────────────────────────────────────────────

  const openPicker = useCallback(async () => {
    setPickerError(null);
    setPickerLoading(true);
    try {
      await loadGapiScript();
      await loadPickerApi();

      // Push picker above Bootstrap modal stack (modals use ~1050-1060)
      let styleEl = document.getElementById("gdrive-picker-zfix");
      if (!styleEl) {
        styleEl = document.createElement("style");
        styleEl.id = "gdrive-picker-zfix";
        styleEl.textContent =
          ".picker-dialog-bg { z-index: 99998 !important; } " +
          ".picker-dialog    { z-index: 99999 !important; }";
        document.head.appendChild(styleEl);
      }

      const P = window.google.picker;
      const view = new P.DocsView()
        .setMimeTypes(mimeTypes(fileType))
        .setIncludeFolders(true);

      const builder = new P.PickerBuilder()
        .addView(view)
        .setOAuthToken(tokenRef.current)
        .setDeveloperKey(PICKER_API_KEY)
        .setCallback((data) => {
          const action = data[P.Response.ACTION];
          if (action === P.Action.PICKED) {
            const docs = data[P.Response.DOCUMENTS] || [];
            (async () => {
              // The Picker's own drive.file-scoped token 404s on a follow-up
              // files.get call (drive.file only grants Picker-UI access, not
              // general metadata reads). Use the broader-scope refresh token
              // instead, same as HomeHeader.js's "Create Journal from Photos".
              let metaToken = null;
              try {
                const refreshToken = localStorage.getItem("gdrive_refresh_token");
                if (refreshToken) {
                  const r = await fetch(`${API_BASE_URL}/gdrive_refresh.php`, {
                    method: "POST",
                    headers: { "Content-Type": "application/x-www-form-urlencoded" },
                    body: `refresh_token=${encodeURIComponent(refreshToken)}`,
                  });
                  const d = await r.json();
                  metaToken = d.access_token || null;
                }
              } catch (_) {}

              const items = await Promise.all(
                docs.map(async (doc) => {
                  const fileId = doc[P.Document.ID];
                  let exif_data = {};
                  let thumb = null;
                  try {
                    const resp = await fetch(
                      `https://www.googleapis.com/drive/v3/files/${fileId}?fields=imageMediaMetadata(location,time),thumbnailLink`,
                      { headers: { Authorization: `Bearer ${metaToken || tokenRef.current}` } }
                    );
                    const meta = await resp.json();
                    const loc = meta.imageMediaMetadata?.location || {};
                    const lat = loc.latitude ?? null;
                    const lon = loc.longitude ?? null;
                    const date = parseDriveTime(meta.imageMediaMetadata?.time);
                    if (lat !== null && lon !== null)
                      exif_data = { latitude: lat, longitude: lon, ...(date ? { date } : {}) };
                    if (meta.thumbnailLink)
                      thumb = meta.thumbnailLink.replace(/=s\d+(-[^?]*)?$/, "=s200");
                  } catch (_) {}
                  if (!thumb) {
                    const pickerThumbs = doc[P.Document.THUMBNAILS] || [];
                    if (pickerThumbs.length > 0) thumb = pickerThumbs[0].url;
                  }
                  return {
                    url:       `gdrive:${fileId}`,
                    url_m:     `gdrive:${fileId}`,
                    thumb,
                    name:      doc[P.Document.NAME] || "",
                    exif_data,
                  };
                })
              );
              if (items.length > 0) onSelect(multi ? items : [items[0]]);
            })();
          }
        });

      if (multi) builder.enableFeature(P.Feature.MULTISELECT_ENABLED);

      builder.build().setVisible(true);
    } catch (e) {
      setPickerError(e.message || "Failed to open picker");
    } finally {
      setPickerLoading(false);
    }
  }, [fileType, multi, onSelect]);

  // ── styles ──────────────────────────────────────────────────────────────────

  const containerStyle = {
    border: "1px solid #dee2e6",
    borderRadius: 6,
    overflow: "hidden",
    marginTop: 8,
  };

  const centeredStyle = {
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", padding: "32px 16px", gap: 12, textAlign: "center",
  };

  const typeLabel = { video: "videos", audio: "audio files", pdf: "PDFs", image: "images" }[fileType] || "files";

  // ── render ──────────────────────────────────────────────────────────────────

  if (waitingForAuth) {
    return (
      <div style={containerStyle}>
        <div style={centeredStyle}>
          <div className="spinner-border spinner-border-sm text-primary" role="status" />
          <strong>Complete sign-in</strong>
          <span style={{ fontSize: "0.85rem", color: "#666" }}>
            A new tab has opened. Sign in with Google, then return here — it will continue automatically.
          </span>
          <button className="btn btn-outline-secondary btn-sm" onClick={cancelAuth}>Cancel</button>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <div style={containerStyle}>
        <div style={centeredStyle}>
          {loading ? (
            <div className="spinner-border spinner-border-sm text-primary" role="status" />
          ) : (
            <>
              <strong>Google Drive</strong>
              <span style={{ fontSize: "0.85rem", color: "#666" }}>
                Sign in to select {typeLabel} from your Google Drive.
              </span>
              <button className="btn btn-primary btn-sm" onClick={startAuth}>
                Sign in with Google
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* toolbar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "4px 10px", borderBottom: "1px solid #dee2e6", background: "#f8f9fa" }}>
        <strong style={{ fontSize: "0.85rem" }}>Google Drive</strong>
        <button className="btn btn-link btn-sm p-0" style={{ fontSize: "0.8rem", color: "#888" }}
          onClick={signOut}>
          Sign out
        </button>
      </div>

      {/* select button */}
      <div style={centeredStyle}>
        {pickerError && (
          <span style={{ fontSize: "0.8rem", color: "#c00" }}>{pickerError}</span>
        )}
        <button
          className="btn btn-primary"
          onClick={openPicker}
          disabled={pickerLoading}
        >
          {pickerLoading
            ? <><span className="spinner-border spinner-border-sm me-2" role="status" />Loading…</>
            : `Select ${typeLabel} from Google Drive`}
        </button>
        <span style={{ fontSize: "0.75rem", color: "#888" }}>
          The files must be shared as "Anyone with the link" in Google Drive.
        </span>
      </div>
    </div>
  );
}
